import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import { Button } from '@/components/Buttons';
import { getProductsQueryOptions } from '@/queries/products';

import ProductEligibilityResult from './ProductEligibilityResult';
import ProductPrice from './ProductPrice';
import useEligibilityBlocked from './useEligibilityBlocked';
import useProductCheckout from './useProductCheckout';

interface ExplorerDayPassProps {
  variant?: 'primary' | 'secondary';
  forEducator?: boolean;
  entryPoint?: string; // for analytics only
}

const ExplorerDayPass = ({ variant = 'primary', forEducator, entryPoint }: ExplorerDayPassProps) => {
  const isPrimaryVariant = variant === 'primary';

  const { data: product } = useQuery({
    ...getProductsQueryOptions({
      lookupKey: 'all-habitats-day-pass',
    }),
    select: (data) => data.list[0],
  });
  const eligibilityBlocked = useEligibilityBlocked(product);
  const { start: startCheckout, isPending } = useProductCheckout();

  return (
    <div
      data-component="all-habitats-day-pass"
      className={clsx(
        'flex w-full max-w-[336px] flex-col items-center justify-center gap-3 rounded-2xl shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]',
        isPrimaryVariant ? 'bg-beige-light' : 'bg-white',
      )}
    >
      <div
        className={clsx(
          'relative w-full rounded-t-2xl bg-[url("https://zoolife.tv/assets/products/l1.webp")] bg-cover bg-center',
          isPrimaryVariant ? 'aspect-[336/143]' : 'aspect-[336/97]',
        )}
      />
      <h4 className="preflight preflight-h4 mx-4 max-w-[274px] text-center text-2xl/7 font-bold text-green-900">
        {forEducator ? 'All-Access Pass & Virtual Field Trip' : 'All-Access Pass & Guided Experience'}
      </h4>
      <ProductPrice currency={product?.price.currency} price={product?.price.price} className="px-4" />
      <p className="preflight preflight-p mx-4 max-w-[274px] text-center text-base/[18px] text-gray-500">
        {forEducator
          ? 'An unforgettable 1-2 hour guided tour with interactive tool-tips. Visit all the habitats, at your students’ pace!'
          : 'Get a virtual tour with interactive tool-tips, and 24h access to all habitats. No need to pick!'}
      </p>
      <div className={clsx('w-full px-4', isPrimaryVariant ? 'pb-6' : 'pb-4')}>
        <Button
          data-component="checkout-button"
          disabled={!product || isPending || eligibilityBlocked}
          loading={isPending}
          size="lg"
          className="w-full"
          onClick={() => {
            if (!product) return;
            startCheckout({
              productId: product._id,
              priceId: product.price.priceId,
              productLookupKey: product.productLookupKey,
              entryPoint,
            });
          }}
        >
          Select Pass
        </Button>
        {product && <ProductEligibilityResult result={product.eligibility} className="mt-3" />}
      </div>
    </div>
  );
};

export default ExplorerDayPass;
